/**
 * Shared "Analytics-style" date range picker.
 *
 * Originally lived inline inside `AnalyticsDashboardDialog.tsx`. Extracted
 * here so it can also be used by the Trainings Overview tab in
 * `ManageEventsDialog.tsx`. The component is a popover-anchored
 * dual-month calendar with a quick-range list on the left.
 *
 * Callers supply their own preset list via `presets` so each surface can
 * advertise its own quick ranges (e.g. analytics has an "All time"
 * lower-bounded at 1 Mar 2026; manage-events does not).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DateRangeIcon from '@mui/icons-material/DateRange';

export type DateRangeState = {
  startDate: string;
  endDate: string;
};

export type DateRangePreset<TId extends string = string> = {
  id: TId;
  label: string;
  build: () => DateRangeState;
};

// ── date helpers ──────────────────────────────────────────────────────

export function formatInputDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseInputDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
}

export function startOfMonthDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addMonthsDate(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

export function addDaysDate(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

export function startOfWeekDate(date: Date): Date {
  return addDaysDate(date, -date.getDay());
}

export function isSameDayDate(left: Date | null, right: Date | null): boolean {
  if (!left || !right) {
    return false;
  }
  return left.getTime() === right.getTime();
}

export function isBeforeDate(left: Date, right: Date): boolean {
  return left.getTime() < right.getTime();
}

export function isWithinRange(date: Date, startDate: Date | null, endDate: Date | null): boolean {
  if (!startDate || !endDate) {
    return false;
  }
  const timestamp = date.getTime();
  return timestamp >= startDate.getTime() && timestamp <= endDate.getTime();
}

export function getMonthLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function buildCalendarDays(month: Date): Array<{ date: Date; inMonth: boolean }> {
  const firstDayOfMonth = startOfMonthDate(month);
  const calendarStart = startOfWeekDate(firstDayOfMonth);
  return Array.from({ length: 42 }, (_, index) => {
    const date = addDaysDate(calendarStart, index);
    return {
      date,
      inMonth: date.getMonth() === month.getMonth(),
    };
  });
}

export function rangesEqual(a: DateRangeState, b: DateRangeState): boolean {
  return a.startDate === b.startDate && a.endDate === b.endDate;
}

export function calendarToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function formatDisplayDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return value;
  }
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

export function formatDisplayRange(range: DateRangeState): string {
  if (!range.startDate || !range.endDate) {
    return 'Select range';
  }
  if (range.startDate === range.endDate) {
    return formatDisplayDate(range.startDate);
  }
  return `${formatDisplayDate(range.startDate)} - ${formatDisplayDate(range.endDate)}`;
}

// ── default preset list ───────────────────────────────────────────────

export type DefaultDateRangePresetId =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'thisMonth'
  | 'lastMonth';

export const DEFAULT_DATE_RANGE_PRESETS: Array<DateRangePreset<DefaultDateRangePresetId>> = [
  {
    id: 'today',
    label: 'Today',
    build: () => {
      const today = calendarToday();
      return { startDate: formatInputDate(today), endDate: formatInputDate(today) };
    },
  },
  {
    id: 'yesterday',
    label: 'Yesterday',
    build: () => {
      const y = addDaysDate(calendarToday(), -1);
      return { startDate: formatInputDate(y), endDate: formatInputDate(y) };
    },
  },
  {
    id: 'last7',
    label: 'Last 7 days',
    build: () => {
      const today = calendarToday();
      const start = addDaysDate(today, -6);
      return { startDate: formatInputDate(start), endDate: formatInputDate(today) };
    },
  },
  {
    id: 'last30',
    label: 'Last 30 days',
    build: () => {
      const today = calendarToday();
      const start = addDaysDate(today, -29);
      return { startDate: formatInputDate(start), endDate: formatInputDate(today) };
    },
  },
  {
    id: 'thisMonth',
    label: 'This month',
    build: () => {
      const today = calendarToday();
      const start = startOfMonthDate(today);
      return { startDate: formatInputDate(start), endDate: formatInputDate(today) };
    },
  },
  {
    id: 'lastMonth',
    label: 'Last month',
    build: () => {
      const today = calendarToday();
      const firstThisMonth = startOfMonthDate(today);
      const lastMonthEnd = addDaysDate(firstThisMonth, -1);
      const lastMonthStart = startOfMonthDate(lastMonthEnd);
      return {
        startDate: formatInputDate(lastMonthStart),
        endDate: formatInputDate(lastMonthEnd),
      };
    },
  },
];

// ── component ─────────────────────────────────────────────────────────

export interface DateRangeSelectorProps {
  label?: string;
  range: DateRangeState;
  onApply: (range: DateRangeState) => void;
  /** Quick-range presets shown on the left rail. Defaults to a sensible
   * common set; surfaces with bespoke presets (e.g. "All time") should
   * pass their own list. */
  presets?: Array<DateRangePreset>;
  /** Optional override for the trigger button width. */
  buttonMinWidth?: number;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  label = 'Date Range',
  range,
  onApply,
  presets = DEFAULT_DATE_RANGE_PRESETS,
  buttonMinWidth = 280,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftRange, setDraftRange] = useState<DateRangeState>(range);
  const [selectionStep, setSelectionStep] = useState<'start' | 'end'>('start');
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => (
    startOfMonthDate(parseInputDate(range.startDate) || new Date())
  ));

  useEffect(() => {
    if (!anchorEl) {
      setDraftRange(range);
      setSelectionStep('start');
      setVisibleMonth(startOfMonthDate(parseInputDate(range.startDate) || new Date()));
    }
  }, [anchorEl, range]);

  const draftStartDate = parseInputDate(draftRange.startDate);
  const draftEndDate = parseInputDate(draftRange.endDate);
  const currentMonth = visibleMonth;
  const nextMonth = addMonthsDate(visibleMonth, 1);
  const todayDate = useMemo(() => calendarToday(), []);

  const activePresetId = useMemo(() => {
    for (const preset of presets) {
      if (rangesEqual(draftRange, preset.build())) {
        return preset.id;
      }
    }
    return null;
  }, [draftRange, presets]);

  const applyPreset = (preset: DateRangePreset) => {
    const next = preset.build();
    setDraftRange(next);
    setSelectionStep('start');
    setVisibleMonth(startOfMonthDate(parseInputDate(next.startDate) || new Date()));
  };

  const handleSelectDate = (selectedDate: Date) => {
    const selectedValue = formatInputDate(selectedDate);

    if (selectionStep === 'start') {
      setDraftRange((current) => {
        const currentEnd = parseInputDate(current.endDate);
        return {
          startDate: selectedValue,
          endDate: !currentEnd || isBeforeDate(currentEnd, selectedDate)
            ? selectedValue
            : current.endDate,
        };
      });
      setSelectionStep('end');
      return;
    }

    if (!draftStartDate) {
      setDraftRange({ startDate: selectedValue, endDate: selectedValue });
      setSelectionStep('end');
      return;
    }

    if (isBeforeDate(selectedDate, draftStartDate)) {
      setDraftRange({
        startDate: selectedValue,
        endDate: draftRange.startDate,
      });
    } else {
      setDraftRange((current) => ({
        ...current,
        endDate: selectedValue,
      }));
    }
    setSelectionStep('start');
  };

  const renderCalendarMonth = (month: Date) => (
    <Box sx={{ flex: 1, minWidth: 280 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, textAlign: 'center', mb: 1.25 }}
      >
        {getMonthLabel(month)}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.75 }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayLabel) => (
          <Typography
            key={`${getMonthLabel(month)}-${dayLabel}`}
            variant="caption"
            sx={{
              textAlign: 'center',
              color: 'rgba(var(--color-text-rgb), 0.5)',
              fontWeight: 700,
              py: 0.25,
            }}
          >
            {dayLabel}
          </Typography>
        ))}

        {buildCalendarDays(month).map(({ date, inMonth }) => {
          const isStart = isSameDayDate(date, draftStartDate);
          const isEnd = isSameDayDate(date, draftEndDate);
          const inRange = isWithinRange(date, draftStartDate, draftEndDate);
          const isToday = isSameDayDate(date, todayDate);
          const isRangeEdge = isStart || isEnd;

          return (
            <Button
              key={formatInputDate(date)}
              size="small"
              onClick={() => handleSelectDate(date)}
              sx={{
                minWidth: 0,
                px: 0,
                py: 0.95,
                borderRadius: isRangeEdge ? 999 : 1.25,
                bgcolor: isRangeEdge
                  ? 'var(--color-accent)'
                  : inRange
                    ? 'rgba(237,137,54,0.18)'
                    : 'transparent',
                color: isRangeEdge
                  ? 'var(--color-text-strong)'
                  : inMonth
                    ? 'rgba(var(--color-text-rgb), 0.9)'
                    : 'rgba(var(--color-text-rgb), 0.36)',
                border: isToday && !inRange ? '1px solid rgba(237,137,54,0.45)' : '1px solid transparent',
                fontWeight: isRangeEdge ? 700 : 500,
                '&:hover': {
                  bgcolor: isRangeEdge
                    ? 'var(--color-accent-strong)'
                    : 'rgba(237,137,54,0.12)',
                },
              }}
            >
              {date.getDate()}
            </Button>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<DateRangeIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          minWidth: buttonMinWidth,
          justifyContent: 'flex-start',
          borderColor: 'rgba(var(--color-text-rgb), 0.18)',
          color: 'rgba(var(--color-text-rgb), 0.92)',
          textTransform: 'none',
          px: 1.75,
          py: 1.15,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.56)' }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatDisplayRange(range)}
          </Typography>
        </Box>
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            width: 920,
            maxWidth: 'calc(100vw - 32px)',
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
            border: '1px solid rgba(var(--color-text-rgb), 0.08)',
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'stretch' }}>
          <Box
            sx={{
              width: 200,
              flexShrink: 0,
              pr: 1.5,
              mr: 1.5,
              borderRight: '1px solid rgba(var(--color-text-rgb), 0.08)',
            }}
          >
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', fontWeight: 700, px: 1, display: 'block', mb: 0.5 }}>
              Quick ranges
            </Typography>
            <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
              {presets.map((preset) => {
                const selected = activePresetId === preset.id;
                return (
                  <ListItemButton
                    key={preset.id}
                    selected={selected}
                    onClick={() => applyPreset(preset)}
                    sx={{
                      borderRadius: 1,
                      py: 0.65,
                      mb: 0.25,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(237,137,54,0.2)',
                        '&:hover': { bgcolor: 'rgba(237,137,54,0.28)' },
                      },
                    }}
                  >
                    <ListItemText
                      primary={preset.label}
                      primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: selected ? 600 : 500 } }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.56)' }}>
                  {selectionStep === 'start' ? 'Select the start date, then the end date.' : 'Select the end date to finish the range.'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setVisibleMonth((current) => addMonthsDate(current, -1))}
                  sx={{ color: 'rgba(var(--color-text-rgb), 0.88)' }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setVisibleMonth((current) => addMonthsDate(current, 1))}
                  sx={{ color: 'rgba(var(--color-text-rgb), 0.88)' }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: 1.5,
                border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDisplayRange(draftRange)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {renderCalendarMonth(currentMonth)}
              {renderCalendarMonth(nextMonth)}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setDraftRange(range);
                  setAnchorEl(null);
                }}
                sx={{ color: 'rgba(var(--color-text-rgb), 0.82)' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  onApply(draftRange);
                  setAnchorEl(null);
                }}
                sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
              >
                Apply
              </Button>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};
